import React, { useRef, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "components/ui/avatar";
import { Button } from "components/ui/button";
import { Upload, Trash2, Camera, Loader2 } from "lucide-react";
import { message } from "antd";
import { toast } from "components/ui/toast";
import { uploadUserAvatar } from "Explore/Services/Settings";

const PRESET_AVATARS = [
  "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80",
  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&auto=format&fit=crop&q=80",
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&auto=format&fit=crop&q=80",
  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&auto=format&fit=crop&q=80",
];

const ALLOWED_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"];

const AvatarUploader = ({ avatarUrl, onAvatarChange, userInitials = "U", onUploadSuccess }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input so user can pick the same file again if desired
    e.target.value = "";

    // Client-side validation
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      toast.error("Only JPEG, PNG, and WebP images are allowed.", { title: "Invalid Format" });
      try { message.error("Only JPEG, PNG, and WebP images are allowed."); } catch (e) {}
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("File size exceeds the 2MB limit.", { title: "File Too Large" });
      try { message.error("File size exceeds the 2MB limit."); } catch (e) {}
      return;
    }

    setUploading(true);
    try {
      const res = await uploadUserAvatar(file);
      if (res && res.avatar_url) {
        const successMsg = res.message || "Avatar uploaded successfully.";
        toast.success(successMsg, { title: "Avatar Updated" });
        try { message.success(successMsg); } catch (e) {}
        onAvatarChange(res.avatar_url);
        if (onUploadSuccess) {
          onUploadSuccess(res.avatar_url);
        }
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (err) {
      console.error("Avatar upload failed:", err);
      const errorDetail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Failed to upload avatar. Please try again.";
      toast.error(errorDetail, { title: "Upload Failed" });
      try { message.error(errorDetail); } catch (e) {}
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    onAvatarChange("");
  };

  const handleSelectPreset = (preset) => {
    onAvatarChange(preset);
  };

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 p-5 rounded-xl border border-gray-200 bg-white">
      <div className="relative group">
        <Avatar className="h-20 w-20 rounded-2xl border-2 border-gray-100 shadow-sm">
          <AvatarImage src={avatarUrl} alt="User Avatar" className="object-cover" />
          <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-violet-600 text-white font-semibold text-xl rounded-2xl">
            {userInitials}
          </AvatarFallback>
        </Avatar>

        {uploading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-white rounded-2xl">
            <Loader2 size={24} className="animate-spin" />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="absolute inset-0 flex items-center justify-center bg-black/40 text-white rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
            title="Upload photo"
          >
            <Camera size={20} />
          </button>
        )}
      </div>

      <div className="flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/png, image/jpeg, image/webp"
            className="hidden"
            disabled={uploading}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="gap-2 text-xs font-medium h-8"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? "Uploading..." : "Upload New Photo"}
          </Button>

          {avatarUrl && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              disabled={uploading}
              className="gap-2 text-xs font-medium h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 size={14} />
              Remove
            </Button>
          )}
        </div>

        <p className="text-xs text-gray-500">
          Recommended: square image, at least 400x400px. JPEG, PNG, or WebP under 2MB.
        </p>

        {/* Preset quick picks */}
        <div className="flex items-center gap-2 pt-1">
          <span className="text-[11px] text-gray-400">Presets:</span>
          {PRESET_AVATARS.map((preset, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => handleSelectPreset(preset)}
              disabled={uploading}
              className="h-6 w-6 rounded-full overflow-hidden border border-gray-200 hover:scale-110 transition-transform disabled:opacity-50"
              title={`Preset avatar ${idx + 1}`}
            >
              <img src={preset} alt="preset" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AvatarUploader;
